'use client';

import { Trash2 } from 'lucide-react';

import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { VerticalReorderingList } from '@/components/vertical-reordering-list';

import OtherOptionField from './other-option-field';
import { AddOptionButton } from './shared/add-option-button';
import { OptionRow } from './shared/option-row';
import { useFieldOptions } from './shared/use-field-options';

interface ISingleSelectFieldProps {
	options: string[];
	selectedOption?: string;
	onSelectOption?: (option: string | undefined) => void;
	onChangeOptions?: (updatedOptions: string[]) => void;
	editMode?: boolean;
	isFocusedInput?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
	disabled?: boolean;
	allowOther?: boolean;
	onAllowOtherChange?: (value: boolean) => void;
}

interface ImperativeHandleProps {
	focusOnFirstOption: () => void;
}

const SingleSelectField = forwardRef<ImperativeHandleProps, ISingleSelectFieldProps>(
	(
		{
			options,
			selectedOption,
			onSelectOption,
			onChangeOptions,
			editMode,
			isFocusedInput,
			onFocus,
			onBlur,
			disabled,
			allowOther = false,
			onAllowOtherChange,
		},
		ref
	) => {
		const [otherSelected, setOtherSelected] = useState(false);

		const {
			optionsCopy,
			displayDeleteButton,
			displayReorderIcon,
			displayAddOption,
			focusOnFirstOption,
			registerOptionRef,
			handleOnReorderOptions,
			handleOnDeleteOption,
			onClickAddOption,
			handleInputOptionChange,
			handleInputOptionFocus,
			handleInputOptionBlur,
			handleInputOptionKeyDown,
		} = useFieldOptions({ options, onChangeOptions, editMode, isFocusedInput, onFocus, onBlur });

		useImperativeHandle(ref, () => ({ focusOnFirstOption }), [focusOnFirstOption]);

		const handleOnSelectOption = (optionValue: string) => {
			onSelectOption?.(optionValue);
			setOtherSelected(false);
		};

		// Radios are keyed by option id, not name, so an option with an empty name can't
		// match the empty "nothing selected" value.
		const selectedOptionId = selectedOption
			? (optionsCopy.find((option) => option.name === selectedOption)?.id ?? '')
			: '';

		const handleOnSelectOptionId = (optionId: string) => {
			const option = optionsCopy.find((item) => item.id === optionId);
			if (option) handleOnSelectOption(option.name);
		};

		const handleOtherOptionChange = useCallback(
			(value: string) => {
				handleOnSelectOption(value);
				setOtherSelected(true);
			},
			[handleOnSelectOption]
		);

		const handleOtherSelect = useCallback(
			(selected: boolean) => {
				handleOnSelectOption('');
				setOtherSelected(selected);
			},
			[handleOnSelectOption]
		);

		return (
			<div className="flex w-full max-w-full flex-col">
				<RadioGroup
					value={selectedOptionId}
					onValueChange={handleOnSelectOptionId}
					disabled={editMode || disabled}
					className="gap-0 p-0"
				>
					<VerticalReorderingList
						items={optionsCopy}
						onReorder={handleOnReorderOptions}
						className="w-full"
						getItemId={(option) => option.id}
						renderItem={(option, index) => (
							<OptionRow
								key={option.id}
								option={option}
								index={index}
								editMode={editMode}
								itemClassName="pl-0"
								leadingControl={
									<RadioGroupItem
										value={option.id}
										id={option.id}
										className="my-[2px] h-5 w-5 self-start border-2 border-slate-700 disabled:opacity-100"
									/>
								}
								showNonEditLabel
								displayDeleteButton={displayDeleteButton}
								displayReorderIcon={displayReorderIcon}
								registerRef={registerOptionRef(option.id)}
								onChangeValue={handleInputOptionChange(option.id)}
								onFocus={handleInputOptionFocus}
								onBlur={handleInputOptionBlur}
								onKeyDown={handleInputOptionKeyDown(option.id)}
								onDelete={handleOnDeleteOption(option.id)}
							/>
						)}
					/>
				</RadioGroup>

				{displayAddOption && (
					<AddOptionButton
						onAddOption={onClickAddOption()}
						onAddOther={() => onAllowOtherChange?.(true)}
						hideOther={allowOther}
					/>
				)}
				{allowOther && (
					<div className="group flex items-start justify-between gap-2">
						<OtherOptionField
							showCheckbox={false}
							disabled={editMode}
							selected={otherSelected && !editMode}
							onSelect={handleOtherSelect}
							onInputChange={(value) => (editMode ? null : handleOtherOptionChange(value))}
						/>

						{displayDeleteButton && (
							<Button
								variant={'ghost'}
								className="opacity-hover:bg-transparent pointer-events-none h-5 w-5 p-0 text-slate-400 opacity-0 group-hover:pointer-events-auto group-hover:opacity-100"
								onClick={() => onAllowOtherChange?.(false)}
							>
								<Trash2 className="h-5 w-5" />
							</Button>
						)}
					</div>
				)}
			</div>
		);
	}
);

SingleSelectField.displayName = 'SingleSelectField';

export default SingleSelectField;
